import React, {
  useRef,
  useCallback,
  memo,
  useState,
  useEffect,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import {
  EditingState,
  TreeDataState,
  CustomTreeData,
  SummaryState,
  IntegratedSummary,
  GroupingState,
  IntegratedGrouping,
} from '@devexpress/dx-react-grid';
import {
  DataTypeProvider,
  FilteringState,
  IntegratedFiltering,
} from '@devexpress/dx-react-grid';
import { GridExporter } from '@devexpress/dx-react-grid-export';
import {
  IntegratedSelection,
  SortingState,
  IntegratedSorting,
  PagingState,
  IntegratedPaging,
} from '@devexpress/dx-react-grid';
import {
  Plugin,
  Template,
  TemplatePlaceholder
} from '@devexpress/dx-react-core';
import {
  Grid,
  VirtualTable,
  TableHeaderRow,
  TableSelection,
  TableTreeColumn,
  TableColumnVisibility,
  ColumnChooser,
  TableSummaryRow,
  TableFilterRow,
  TableBandHeader,
  ExportPanel,
  Toolbar,
  DragDropProvider,
  TableGroupRow,
  GroupingPanel,
  PagingPanel,
  Table,
  TableEditRow,
  TableEditColumn,
  TableColumnReordering,
  TableFixedColumns,
  TableInlineCellEditing
} from '@devexpress/dx-react-grid-material-ui';
import _ from 'lodash';
import saveAs from 'file-saver';
import { jsPDF } from 'jspdf';
import autoTable from "jspdf-autotable";
import moment from 'moment';
import { onError } from '../../components/shared/messages';
import { useLoader } from '../../hooks/useLoader';
import { PopupEditing } from './popupEditing';
import './style.css';
//import './arial-normal.js';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { Button, Icon, Input, Popup } from 'semantic-ui-react';
import { useTextResources } from '../../language';
import { SelectionState } from './selectionState';

const GridPanel = ({ options, data, onUpdate, onSelected }) => {
  const exporterRef = useRef();
  const loader = useLoader();
  const [showFilter, setShowFilter] = useState(options.showFilter);
  const [expandedGroups, setExpandedGroups] = useState(options?.customOptions?.group?.expandedGroups || []);

  const selection = useRef([])

  const text = useTextResources()
  if (options.customOptions) {
    options.initialCustomOptions = JSON.parse(JSON.stringify(options.customOptions));
  }

  if (options.customOptions?.order && !options.customOptions?.order?.ordering) {
    options.customOptions.order.ordering = options.columns.map((x) => x.name);
  }
  if (
    options.customOptions?.hidden &&
    !options.customOptions?.hidden?.hiddenColumns
  ) {
    options.customOptions.hidden.hiddenColumns = [];
  }

  const localStorageState = useLocalStorageState(
    `${options.cacheId}-GridOptions-${window.user?.UserName}`,
    options.customOptions
  );
  const regularState = useState(options.customOptions);

  const [customOptions, setCustomOptions] = options.cacheId
    ? localStorageState
    : regularState;

  useEffect(() => {
    if (
      options?.customOptions?.overrideData &&
        _.isEqual(options.customOptions, customOptions)
        /*options.customOption will be new every time the parent page rerenders (unless wrapped in useMemo) and thus this
         useEffect will rerun and revert the gridPanel to its original custom options, reverting any changes made since then.
        This, combined with customOptions.overrideData will deep check and verify if the object had actually changed and only if so, set the new custom options */
    ) {
      setCustomOptions(options.customOptions);
    }
  }, [options.customOptions]);

  const excelHiddenColumnNames = useMemo(
    () => (options.excel?.hiddenColumns || []).map((c) => c.columnName),
    [options.excel?.hiddenColumns]
  );

  const groupingChange = (groups) => {
    const newCustomOptions = { ...customOptions };
    newCustomOptions.group.grouping = groups;
    setCustomOptions(newCustomOptions);
  }

  const collapseAllGroups = () => setExpandedGroups([]);

  const unfoldAllGroups = () => {
    // Retrieve current active grouping columns from the configuration.
    const currentGroupingNames = customOptions?.group?.grouping?.map(
      (g) => g.columnName
    );

    if (currentGroupingNames) {
      // Initialize a set to store unique group keys.
      const generatedGroupKeysSet = new Set();

      // Iterate through each item in the data array.
      data.forEach((item) => {
        let currentKey = '';

        // Iterate through each column name that is actively used for grouping.
        currentGroupingNames.forEach((field, index) => {
          // For all except the first item, prepend with '|' to separate group levels as this is the way DevExpress Grid operates.
          if (index > 0) {
            currentKey += '|';
          }
          // Append the item's value under the field to the currentKey.
          currentKey += item[field];
          // Add the complete key for the current grouping level to the set.
          generatedGroupKeysSet.add(currentKey);
        });
      });

      // Convert the set of keys to an array and update the state to expand all groups.
      setExpandedGroups([...generatedGroupKeysSet]);
    }
  };



  const orderingChange = (order) => {
    const newCustomOptions = { ...customOptions };
    newCustomOptions.order.ordering = order;
    setCustomOptions(newCustomOptions);
  };

  const hiddenColumnsChange = (columns) => {
    let newCustomOptions = { ...customOptions };
    newCustomOptions.hidden.hiddenColumns = columns.map((x) => ({
      columnName: x,
      togglingEnabled: newCustomOptions.hidden.hiddenColumns.find(
        (c) => c.columnName == x
      )?.togglingEnabled,
    }));
    setCustomOptions(newCustomOptions);
  };

  const commitChanges = ({ added, changed, deleted }) => {
    onUpdate?.(added, changed, deleted)
  };

  const deleteMultiple = () => {
    commitChanges({ deleted: selection.current })
  }

  const onSelectionChange = (value) => {
    selection.current = value
    onSelected?.(value)
  }

  const CustomCell = memo((props) => {
    const cell = <VirtualTable.Cell {...props}></VirtualTable.Cell>;
    if (options.customCellCallback) {
      const custom = options.customCellCallback(props);
      return custom ? <VirtualTable.Cell>{custom}</VirtualTable.Cell> : cell;
    }
    return cell;
  });
  CustomCell.displayName = 'CustomCell';

  const CustomNoDataCell = memo((props) => {
    const cell = <VirtualTable.Cell {...props}></VirtualTable.Cell>;
    if (options.customNoDataCell) {
      const custom = options.customNoDataCell(props);
      return custom ? <VirtualTable.Cell>{custom}</VirtualTable.Cell> : cell;
    }
    return cell;
  });
  CustomNoDataCell.displayName = 'CustomNoDataCell';

  const CustomTreeCell = memo((props) => {
    const cell = <TableTreeColumn.Cell {...props}></TableTreeColumn.Cell>;
    if (options.customTreeCellCallback) {
      const custom = options.customTreeCellCallback(props);
      return custom ? (
        <TableTreeColumn.Cell>{custom}</TableTreeColumn.Cell>
      ) : (
        cell
      );
    }
    return cell;
  });
  CustomTreeCell.displayName = 'CustomTreeCell';

  const CustomSelectionCell = memo((props) => {
    const selectable = options.selectableCallback
      ? options.selectableCallback(props.row)
      : true;
    return selectable ? (
      <TableSelection.Cell {...props} />
    ) : (
      <VirtualTable.Cell {...props}>
        {options.noSelectionCellContent}
      </VirtualTable.Cell>
    );
  });
  CustomSelectionCell.displayName = 'CustomSelectionCell';

  const CustomRow = memo((props) => {
    const className = options.rowClassCallback?.(props.row) || '';
    return (
      <VirtualTable.Row
        {...props}
        className={className}
        height={50}
      ></VirtualTable.Row>
    );
  });
  CustomRow.displayName = 'CustomRow';

  const CustomEditor = (props) => {
    
    let { value, column, onValueChange, ...rest } = props;
    if (column.valueField) {
      if (_.isArray(value)) {
        value = _.map(value, (v) => v[column.valueField] || v);
      } else if (_.isFunction(column.valueField)) {
        value = column.valueField(value);
      } else if (value) {
        value = value[column.valueField];
      }
    }
    let customprops = {};
    if (column.customProps) {
      if (_.isFunction(column.customProps)) {
        customprops = column.customProps(props);
      } else {
        customprops = column.customProps;
      }
    }
    return React.cloneElement(column.editor, {
      value,
      onChange: (newValue, data) => {
        if (data?.value) {
          newValue = data?.value;
        }
        onValueChange(newValue);
        if (column.editor.props.onChange) {
          column.editor.props.onChange(newValue);
        }
      },
      ...rest,
      ...customprops,
    });
  };
  CustomEditor.displayName = 'CustomEditor';
  CustomEditor.propTypes = {
    onValueChange: PropTypes.func,
    value: PropTypes.any,
  };

  const CustomFormatter = memo((props) => {
    const { column, row, value } = props;
    if (column.formatter) {
      return column.formatter(props);
    } else if (_.isArray(value)) {
      return _.join(
        _.map(value, (item) => {
          if (_.isObject(item)) {
            if (item[column.displayField]) {
              return item[column.displayField];
            } else {
              //"Could not find:" + column.displayField
              ('Please select');
            }
          } else {
            return item;
          }
        }),
        ','
      );
    } else if (column.displayField) {
      if (_.isObject(value) && value[column.displayField]) {
        return value[column.displayField];
      } else if (row[column.displayField]) {
        return row[column.displayField];
      } else {
        //return "Could not find:" + column.displayField
        return 'Please select';
      }
    } else {
      return value;
    }
  });
  CustomFormatter.displayName = 'CustomFormatter';
  CustomFormatter.propTypes = {
    value: PropTypes.any,
  };

  const CustomEditorProvider = memo((props) => {
    return (
      <DataTypeProvider
        editorComponent={CustomEditor}
        formatterComponent={CustomFormatter}
        {...props}
      />
    );
  });
  CustomEditorProvider.displayName = 'CustomEditorProvider';

  const CustomValueFormatter = memo((props) => {
    const { formatter, ...rest } = props;
    return <DataTypeProvider formatterComponent={formatter} {...rest} />;
  });
  CustomValueFormatter.displayName = 'CustomValueFormatter';
  CustomValueFormatter.propTypes = {
    formatter: PropTypes.any,
  };

  const CustomToolbarMarkup = ({ children }) => (
    <Plugin name='customToolbarMarkup'>
      <Template name='toolbarContent'>
        <TemplatePlaceholder />
        {children}
      </Template>
    </Plugin>
  );
  CustomToolbarMarkup.propTypes = {
    children: PropTypes.any,
  };

  const CustomGroupRowSummaryItem = (row) => {
    const columnName = row.children?.props?.column?.name;
    const hideTypeName = options.groupSummaryItems.find(
      (x) => x.columnName === columnName
    )?.hideTypeName;
    const rowValue = row.children?.props?.column?.formatter?.(row) || row.value;
    return hideTypeName || typeof rowValue === 'object'
      ? rowValue
      : `${text(
        row.type.charAt(0).toUpperCase() + row.type.slice(1)
      )}: ${rowValue}`;
  };

  const printPdf = useCallback(async () => {
    try {
      loader(true);
      const doc = new jsPDF({
        orientation: 'landscape',
      });
      doc.setFont('arial');
      const userLang = window.optima?.user?.UserSettings[0]?.Language?.LocaleID;
      const rtl = userLang === 'he-IL';
      doc.setR2L(rtl);

      const cols = options.columns.filter((col) => !col.excludePdf);

      const getColNameFormatted = (row, col) => {
        let value = row[col.name];
        if (col.formatter) {
          value = col.formatter({ value, row });
        }
        if (col.getCellValue) {
          value = col.getCellValue(row, col.name);
        }
        if (col.html) {
          value = row[col.name]?.props?.dangerouslySetInnerHTML?.__html;
        }
        return value;
      };

      const tables = _.groupBy(data, (row) => {
        const groups = customOptions?.group?.grouping.map((x) => x.columnName);
        const columns = cols.filter((x) => groups.includes(x.name));
        const titles = columns.map(
          (col) => `${col.title}: ${getColNameFormatted(row, col)}`
        );
        return titles.reverse().join(', ');
      });

      const filteredColumns = cols
        .filter(
          (col) =>
            !customOptions?.group?.grouping?.some(
              (g) => g.columnName == col.name
            )
        )
        .filter(
          (col) =>
            !customOptions?.hidden?.hiddenColumns?.some(
              (c) => c.columnName == col.name
            )
        );

      const keys = Object.keys(tables);

      const transformData = (data) => {
        return data.map((row) => {
          return filteredColumns.map((col) => getColNameFormatted(row, col));
        });
      };

      keys.forEach((key) => {
        const lastTableY = doc.lastAutoTable?.finalY;
        const textY = lastTableY ? lastTableY + 10 : 20;

        doc.text(key, 14, textY, {
          isInputRtl: rtl,
          isOutputRtl: rtl,
          isInputVisual: rtl,
        });

        autoTable(doc, {
          styles: { font: "arial" },
          head: [filteredColumns.map((v) => v.title)],
          body: transformData(tables[key]),
          startY: lastTableY ? lastTableY + 15 : 25,
        });
      });

      doc.save(`${options.title}-${moment().format('YYYY-MM-DD')}.pdf`);
      loader(false);
    } catch (error) {
      onError(error);
    } finally {
      loader(false);
    }
  }, [
    data,
    options.title,
    customOptions?.group?.grouping,
    customOptions?.hidden?.hiddenColumns,
  ]);

  const ExportPDFButton = () => (
      <Popup content='PDF' position='bottom center' mouseEnterDelay={200} trigger={
        <span style={{ display: 'inline-block', cursor: 'pointer' }} onClick={printPdf}>
          <Icon name='file pdf' />
        </span>
      } />
  );

  const ExportExcelButton = ({ onToggle, ...params }) => {
    return (
        <Popup content='Excel' position='bottom center' mouseEnterDelay={200} trigger={
          <span style={{ display: 'inline-block', cursor: 'pointer' }} onClick={onToggle}>
            <Icon name='file excel' {...params} />
          </span>
        } />
    );
  };

  const ShowFilterButton = () => (
      <Popup content='Show Filter' position='bottom center' mouseEnterDelay={200} trigger={
        <span style={{ display: 'inline-block', cursor: 'pointer' }} onClick={() => setShowFilter(!showFilter)}>
          <Icon name='filter' />
        </span>
      } />
  );

  const ShowCollapseAndUnfoldAllButton = () => (
      <Popup content={expandedGroups.length ? text('CollapseAllGroups') : text('UnfoldAllGroups')} position='bottom center' mouseEnterDelay={200} trigger={
        <span style={{ display: 'inline-block', cursor: 'pointer' }} onClick={expandedGroups.length ? collapseAllGroups : unfoldAllGroups}>
          <Icon name={expandedGroups.length ? 'angle up' : 'angle down'} />
        </span>
      } />
  );

  const ResetCustomOptionsButton = () => (
      <Popup content='Reset' position='bottom center' mouseEnterDelay={200} trigger={
        <span style={{ display: 'inline-block', cursor: 'pointer' }} onClick={() => setCustomOptions(options.initialCustomOptions)}>
          <Icon name='refresh' />
        </span>
      } />
  );

  const onSave = (workbook) => {
    workbook.xlsx.writeBuffer().then((buffer) => {
      saveAs(
        new Blob([buffer], { type: 'application/octet-stream' }),
        `${options.title}.xlsx`
      );
    });
  };

  const TableEditColumnCommandComponent = (props) => {
    // eslint-disable-next-line react/prop-types
    const { id, onExecute } = props;
    if (id == "add") {
      const buttonText = options.editable?.addButtonText || text("New");
      return <Button positive onClick={onExecute}>{buttonText}</Button>
    } else {
      return <TableEditColumn.Command {...props}></TableEditColumn.Command>
    }
  }

  const TableEditColumnHeaderCell = (props) => {
    return (
      <TableEditColumn.HeaderCell {...props}>
        {
          
          props.children
        }
        {options.showDeleteMultiple && (
          <Button id='delete-many' negative onClick={deleteMultiple} icon="trash">
          </Button>
        )}
        {options.additionalCommands}
      </TableEditColumn.HeaderCell>
    );
  };

  const startExport = useCallback(() => {
    exporterRef.current.exportGrid();
  }, [exporterRef]);

  const Root = React.useMemo(() => 
    // eslint-disable-next-line react/display-name
    (props) => <Grid.Root {...props} style={{ flex: 1, overflow: 'hidden' }} className='grid-root' />, 
  []);

  const toolbar =
    options.export || customOptions?.group || customOptions?.hidden || options.menu || options.pdf || options.editable?.inline;

  const groupRow = options.leftColumns || customOptions?.group;

  return (
    <React.Fragment>
      <Grid
        rootComponent={Root}
        rows={data || []}
        columns={options.columns}
        getRowId={options.getRowId}
      >
        {options.columns
          .filter((column) => column.formatter)
          .map((column) => {
            return (
              <CustomValueFormatter
                key={column.name}
                for={[column.name]}
                formatter={column.formatter}
              />
            );
          })}

        {options.columns
          .filter((column) => column.editor)
          .map((column) => {
            return (
              <CustomEditorProvider
                key={column.name}
                for={[column.name]}
                editor={column.editor}
              />
            );
          })}

        {options.tree && (
          <TreeDataState
            defaultExpandedRowIds={options.tree.defaultExpandedRows}
            onExpandedRowIdsChange={options.tree.onExpandedRowIdsChange}
          />
        )}
        {options.tree && (
          <CustomTreeData getChildRows={options.tree.getChildRows} />
        )}

        {customOptions?.sort && (
          <SortingState defaultSorting={customOptions?.sort.sorting} />
        )}
        {customOptions?.sort && <IntegratedSorting columnExtensions={customOptions?.sort.compare} />}

        {customOptions?.group && <DragDropProvider />}
        {customOptions?.group && (
          <GroupingState
            grouping={customOptions?.group?.grouping || []}
            expandedGroups={expandedGroups}
            defaultGrouping={customOptions?.group?.grouping || []}
            onExpandedGroupsChange={setExpandedGroups}
            defaultExpandedGroups={
              customOptions?.group?.defaultExpandedGroups || []
            }
            columnExtensions={customOptions?.group.extensions || []}
            onGroupingChange={groupingChange}
          />
        )}
        {customOptions?.group && <IntegratedGrouping columnExtensions={customOptions?.group.criteria} />}

        {options.summary && (
          <SummaryState
            totalItems={options.totalSummaryItems}
            groupItems={options.groupSummaryItems}
          />
        )}
        {options.summary && <IntegratedSummary calculator={options.summaryCalculator || IntegratedSummary.defaultCalculator} />}

        {options.filtering && showFilter && (
          <FilteringState
            defaultFilters={showFilter ? options?.filtering?.defaults : []}
            columnExtensions={options.filtering.extensions}
          />
        )}
        {options.filtering && (
          <IntegratedFiltering
            columnExtensions={options.filtering.extensions}
          />
        )}

        {options.selectable && <SelectionState onSelectionChange={onSelectionChange} data={data || []} />}
        {options.selectable && <IntegratedSelection />}

        {options.paging && (
          <PagingState
            defaultCurrentPage={0}
            pageSize={options.paging.pageSize || 50}
          />
        )}
        {options.paging && <IntegratedPaging />}

        {options.editable && (
          <EditingState
            onCommitChanges={commitChanges}
            columnExtensions={options.columnExtensions}
            {...options.editable.extraProps}
          />
        )}

        {options.noVirtualTable ? (
          <Table
            cellComponent={CustomCell}
            rowComponent={CustomRow}
            noDataCellComponent={CustomNoDataCell}
            columnExtensions={options.columnExtensions}
          />
        ) : (
          <VirtualTable
            cellComponent={CustomCell}
            rowComponent={CustomRow}
            noDataCellComponent={CustomNoDataCell}
            columnExtensions={options.columnExtensions}
            height='auto'
          />
        )}
        {!options.hideHeader && <TableHeaderRow showSortingControls={customOptions?.sort} />}
        <TableColumnVisibility
          defaultHiddenColumnNames={customOptions?.hidden?.hiddenColumns?.map(
            (x) => x.columnName
          )}
          hiddenColumnNames={customOptions?.hidden?.hiddenColumns?.map(
            (x) => x.columnName
          )}
          columnExtensions={customOptions?.hidden?.columnsExtensions}
          onHiddenColumnNamesChange={hiddenColumnsChange}
        />
        {options.selectable && (
          <TableSelection
            showSelectAll
            showSelectionColumn
            cellComponent={CustomSelectionCell}
          />
        )}
        {options.columnBands && (
          <TableBandHeader columnBands={options.columnBands} />
        )}
        {options.tree && (
          <TableTreeColumn
            for={options.tree.treeColumnName}
            cellComponent={CustomTreeCell}
          />
        )}
        {options.summary && <TableSummaryRow />}
        {options.editable && <TableEditRow />}
        {options.editable && (options.editable?.allowAdd || options.editable?.allowEdit || options.editable?.allowDelete) && (
          <TableEditColumn
            showAddCommand={options.editable?.allowAdd}
            showEditCommand={options.editable?.allowEdit}
            showDeleteCommand={options.editable?.allowDelete}
            headerCellComponent={TableEditColumnHeaderCell}
            commandComponent={TableEditColumnCommandComponent}
          />
        )}
        {options.editable?.inline && <TableInlineCellEditing selectTextOnEditStart />}
        {options.editable?.modalEditor && (
          <PopupEditing
            popupComponent={options.editable.modalEditor.modal}
            extendedProps={options.editable.modalEditor.props}
          />
        )}

        {customOptions?.order && (
          <TableColumnReordering
            order={customOptions?.order.ordering || []}
            onOrderChange={orderingChange}
          />
        )}
        {groupRow && (
          <TableGroupRow summaryItemComponent={CustomGroupRowSummaryItem} />
        )}
        {options.filtering && showFilter && (
          <TableFilterRow cellComponent={CustomTableFilterRowCell} />
        )}
        {options.leftColumns && (
          <TableFixedColumns leftColumns={options.leftColumns} />
        )}
        {toolbar && <Toolbar />}
        {customOptions?.group && <GroupingPanel />}
        {options.menu && (
          <CustomToolbarMarkup>{options.menu}</CustomToolbarMarkup>
        )}
        {customOptions?.hidden?.toggleHidden && <ColumnChooser />}
        {options.export && (
          <ExportPanel
            startExport={startExport}
            toggleButtonComponent={ExportExcelButton}
          />
        )}
        {options.paging && <PagingPanel />}
        {options.pdf && (
          <CustomToolbarMarkup>
            <ExportPDFButton />
          </CustomToolbarMarkup>
        )}
        {options.initialCustomOptions?.group && (
          <CustomToolbarMarkup>
            <ShowCollapseAndUnfoldAllButton />
          </CustomToolbarMarkup>
        )}

        {options.filtering && (
          <CustomToolbarMarkup>
            <ShowFilterButton />
          </CustomToolbarMarkup>
        )}


        {(options.initialCustomOptions?.group ||
          options.initialCustomOptions?.hidden ||
          options.initialCustomOptions?.order) &&

          <CustomToolbarMarkup>
            <ResetCustomOptionsButton />
          </CustomToolbarMarkup>}
      </Grid>
      <GridExporter
        ref={exporterRef}
        rows={data || []}
        columns={options.columns
          .filter(
            (c) => !excelHiddenColumnNames.includes(c.name) //Filter hidden columns from Excel file
          )
          .map((x) => {
            return {
              ...x,
              title: x.exportTitle || x.title,
            };
          })}
        {...options.excel}
        {...{ grouping: options?.customOptions?.group?.grouping }} //Added dynamic excel grouping by user's choice
        onSave={onSave}
      ></GridExporter>
    </React.Fragment>
  );
};

const CustomTableFilterRowCell = ({
  column,
  filter,
  onFilter,
  filteringEnabled
}) => {
  if (column.customFilter) {
    const CustomFilter = column.customFilter;
    return (
      <Table.Cell>
        <CustomFilter
          value={filter ? filter.value : ''}
          onChange={value =>
            onFilter({ value })
          }
        />
      </Table.Cell>
    );
  }

  const regularFilter = (
    <Input
      style={{ padding: 5 }}
      fluid
      transparent
      placeholder='Filter...'
      value={filter?.value || ''}
      onChange={(e, { value }) => onFilter(value ? { value } : null)}
    />
  );

  return filteringEnabled ? (
    <Table.Cell>{regularFilter}</Table.Cell>
  ) : <Table.Cell />;
};

CustomTableFilterRowCell.propTypes = {
  filter: PropTypes.object,
  onFilter: PropTypes.func,
  customFilter: PropTypes.func,
  column: PropTypes.object,
  columnExtensions: PropTypes.array,
  filteringEnabled: PropTypes.bool,
};

GridPanel.propTypes = {
  options: PropTypes.any,
  data: PropTypes.any,
  column: PropTypes.any,
  onUpdate: PropTypes.func,
  onUncommittedRowChange: PropTypes.func,
  onSelected: PropTypes.func,
  style: PropTypes.any,
  row: PropTypes.any,
  title: PropTypes.string,
  shit: PropTypes.any
};
export default GridPanel;
